import { ArrowRight, Check, Loader2, TrendingDown, TrendingUp, Undo2, X } from 'lucide-react'
import { Badge } from '../../lib/shadcn/badge'
import { Button } from '../../lib/shadcn/button'
import { TableCell, TableRow } from '../../lib/shadcn/table'
import { cn } from '../../lib/shadcn/utils'
import type { TierReviewItem } from '../../utils/tierReview'

export type TierDecision = 'approve' | 'skip' | 'undo'

const num = (n: number | null | undefined, digits = 1) =>
  n == null ? '–' : n.toLocaleString('en-US', { maximumFractionDigits: digits })

function TierBadge({ tier, muted = false }: { tier: string | null; muted?: boolean }) {
  return (
    <Badge variant="outline" className={cn('whitespace-nowrap font-medium', muted && 'text-muted-foreground')}>
      {tier ?? 'No tier'}
    </Badge>
  )
}

// One flavor in the tier review: its tier, the numbers behind the
// suggestion, why, and Approve / Skip (or the decision with Undo).
export default function TierReviewRow({
  item,
  busyDecision,
  disabled,
  onDecide,
}: {
  item: TierReviewItem
  /** The action running on this row, so only that button shows a spinner. */
  busyDecision: TierDecision | null
  disabled: boolean
  onDecide: (item: TierReviewItem, decision: TierDecision) => void
}) {
  const m = item.metrics
  const change = m.change_pct ?? null
  const appliedTier = item.applied_tier ?? item.suggested_tier
  const decidedOn = item.decided_at
    ? new Date(item.decided_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    : null
  const decidedBy = item.decided_by?.includes('@') ? item.decided_by.split('@')[0] : item.decided_by

  return (
    <TableRow className={cn(item.is_change && item.decision === 'pending' && 'bg-primary/5')}>
      <TableCell className="align-top whitespace-nowrap">
        <div className="font-medium">{item.flavor}</div>
        {m.first_sold && m.period?.[0] && m.first_sold >= m.period[0].start && (
          <div className="text-xs text-muted-foreground">New: first sold {m.first_sold}</div>
        )}
      </TableCell>

      <TableCell className="align-top whitespace-nowrap">
        {item.is_change ? (
          <div className="flex flex-nowrap items-center gap-1">
            <TierBadge tier={item.current_tier} muted />
            <ArrowRight className="h-3.5 w-3.5 text-muted-foreground" />
            <Badge className="whitespace-nowrap">
              {item.decision === 'approved' ? appliedTier : item.suggested_tier}
            </Badge>
          </div>
        ) : (
          <div className="flex items-center gap-1.5">
            <TierBadge tier={item.current_tier} />
            <span className="text-xs text-muted-foreground">{item.current_tier ? 'Keep' : ''}</span>
          </div>
        )}
      </TableCell>

      <TableCell className="align-top text-right tabular-nums">
        <div className="font-medium">{num(m.units, 0)}</div>
        <div className="text-xs text-muted-foreground whitespace-nowrap">
          S {num(m.small, 0)} · L {num(m.large, 0)}
          {(m.other ?? 0) > 0 && ` · ${num(m.other, 0)} other`}
        </div>
      </TableCell>

      <TableCell className="align-top text-right tabular-nums">
        <div className="font-medium">{num(m.per_week)}</div>
        {change != null ? (
          <div
            className={cn(
              'flex items-center justify-end gap-0.5 text-xs whitespace-nowrap',
              change > 0
                ? 'text-emerald-600 dark:text-emerald-500'
                : change < 0
                  ? 'text-amber-600 dark:text-amber-500'
                  : 'text-muted-foreground',
            )}
            title={`${num(m.prev_per_week)}/week ${m.prev_label ?? ''}`}
          >
            {change > 0 ? <TrendingUp className="h-3 w-3" /> : change < 0 ? <TrendingDown className="h-3 w-3" /> : null}
            {change > 0 ? '+' : ''}
            {change}% vs {m.prev_label}
          </div>
        ) : (
          <div className="text-xs text-muted-foreground whitespace-nowrap">No earlier sales to compare</div>
        )}
        {m.slowing && (
          <Badge variant="warning" className="mt-1">
            Slow mover
          </Badge>
        )}
      </TableCell>

      <TableCell className="align-top text-right tabular-nums">{num(m.orders, 0)}</TableCell>

      <TableCell className="align-top text-right tabular-nums">
        <div>{num(m.retailers, 0)}</div>
        {m.top_retailer && m.top_retailer_pct != null && (m.retailers ?? 0) > 1 && (
          <div className="text-xs text-muted-foreground max-w-[10rem] truncate ml-auto" title={m.top_retailer}>
            Top {m.top_retailer_pct}%: {m.top_retailer}
          </div>
        )}
      </TableCell>

      <TableCell className="align-top min-w-[18rem] max-w-md">
        <ul className="space-y-0.5 text-xs text-muted-foreground">
          {item.reasons.map(r => (
            <li key={r}>{r}</li>
          ))}
        </ul>
        {item.sync_error && <p className="mt-1 text-xs text-destructive">{item.sync_error}</p>}
      </TableCell>

      <TableCell className="align-top whitespace-nowrap">
        {!item.is_change ? null : item.decision === 'pending' ? (
          <div className="flex justify-end gap-1.5">
            <Button
              size="sm"
              className="h-8 gap-1"
              disabled={disabled}
              onClick={() => onDecide(item, 'approve')}
            >
              {busyDecision === 'approve' ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Check className="h-3.5 w-3.5" />
              )}
              {busyDecision === 'approve' ? 'Approving…' : 'Approve'}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="h-8 gap-1"
              disabled={disabled}
              onClick={() => onDecide(item, 'skip')}
            >
              {busyDecision === 'skip' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <X className="h-3.5 w-3.5" />}
              {busyDecision === 'skip' ? 'Skipping…' : 'Skip'}
            </Button>
          </div>
        ) : (
          <div className="flex flex-col items-end gap-1">
            <Badge
              variant={item.decision === 'approved' ? 'success' : 'secondary'}
              className="whitespace-nowrap"
              title={decidedBy ? `${item.decision === 'approved' ? 'Approved' : 'Skipped'} by ${decidedBy}` : undefined}
            >
              {item.decision === 'approved' ? `Approved: ${appliedTier}` : 'Skipped'}
            </Badge>
            {decidedOn && <span className="text-[11px] text-muted-foreground">{decidedOn}</span>}
            <Button
              size="sm"
              variant="ghost"
              className="h-7 gap-1 px-2 text-xs"
              disabled={disabled}
              onClick={() => onDecide(item, 'undo')}
              title={item.decision === 'approved' ? `Put ${item.current_tier ?? 'the old tier'} back` : 'Back to pending'}
            >
              {busyDecision === 'undo' ? <Loader2 className="h-3 w-3 animate-spin" /> : <Undo2 className="h-3 w-3" />}
              Undo
            </Button>
          </div>
        )}
      </TableCell>
    </TableRow>
  )
}
